import { useCallback, useContext, useEffect, useRef, useState } from "react";
import SearchForm from "./SearchForm";
import useSearch from "./useSearch";
import { useStyles } from "../../../../Styles/styles";
import { UserContext } from "../../../../Contexts/UserContext";
import { SetupContext } from "../../../../Contexts/SetupContext";
import SearchModal from "../../../SearchModal";
import SearchButtons from "../../../SearchButtons";
import PaginatedTableWrapper from "../../../PaginatedTableWrapper";
import {
  parseSizeAndWeightSearch,
  sizeAndWeightSearchHeaders,
} from "../../../../utils/SearchHelpers";
import { useLazyQuery } from "@apollo/client";
import { ALL_OFFICERS_QUERY } from "../../../../Queries/AllOfficersQuery";
import { localGetData, localStoreData } from "../../../../utils/utils";
import moment from "moment";

const SizeAndWeightSearchContainer = ({
  showModal,
  setShowModal,
  handleIncidentOpening,
  handleTicketOpening,
  useSearchHook = useSearch,
  SearchFormComponent = SearchForm,
}) => {
  const classes = useStyles();
  const { user, onlineState } = useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const [showSearchOptions, setShowSearchOptions] = useState(true);
  const {
    searchResults,
    setSearchResults,
    searchState,
    resultsPerPage,
    setResultsPerPage,
    handleSearch,
    handleChange,
    handleReset,
    loading,
    error,
  } = useSearchHook(user.id, user.homeOriId?.toString(), () =>
    setShowSearchOptions(false)
  );

  const [officers, setOfficers] = useState([]);

  const [getOfficers] = useLazyQuery(ALL_OFFICERS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        localStoreData(data, `mp-${user?.homeOriId}-officers`);
        checkForOfficers();
      }
    },
  });

  const handleSubmit = e => {
    e?.preventDefault();
    handleSearch(searchState);
  };

  const handlePagePerResultAndPageChange = (newResultsPerPage, newPage) => {
    handleSearch(searchState, newResultsPerPage, newPage);
  };

  const checkForOfficers = useCallback(() => {
    if (localGetData(`mp-${user.homeOriId}-officers`)) {
      // console.log('Found the officers in local storage')
      const officers = localGetData(`mp-${user.homeOriId}-officers`);
      const now = moment();
      const age = now.diff(officers.timestamp, "hours");
      if (age > 8 && onlineState) {
        // console.log('Officers were stale')
        localStorage.removeItem(`mp-${user.homeOriId}-officers`);
        getOfficers({ variables: { oriIds: user.homeOriId?.toString() } });
      } else {
        setOfficers(localGetData(`mp-${user.homeOriId}-officers`)?.allOfficers);
      }
    } else {
      // console.log('Fetching the officers')
      getOfficers({ variables: { oriIds: user.homeOriId?.toString() } });
    }
  }, [getOfficers, onlineState, user.homeOriId]);

  const dashRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: dashRef?.current?.offsetWidth,
    height: dashRef?.current?.offsetHeight,
  });

  const [windowSize, setWindowSize] = useState({
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      dashRef.current &&
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDashSize({
      width: dashRef?.current?.offsetWidth,
      height: dashRef?.current?.offsetHeight,
    });
  }, [dashRef, showModal]);

  useEffect(() => {
    if (showModal && user?.homeOriId && onlineState) {
      checkForOfficers();
    }
  }, [showModal, checkForOfficers, user?.homeOriId, onlineState]);

  return (
    showModal && (
      <SearchModal showModal={showModal} setShowModal={setShowModal}>
        <form
          id="size-and-weight-search-form"
          className={classes.paper}
          ref={dashRef}
          style={{ display: "flex", flexDirection: "column", padding: 0 }}
        >
          <SearchFormComponent
            searchState={searchState}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            inputLabel={null}
            classes={classes}
            nightMode={user.nightMode}
            setup={setup}
            showSearchOptions={showSearchOptions}
            setShowSearchOptions={setShowSearchOptions}
            SearchButtonsComponent={SearchButtons}
            loading={loading}
            setSearchResults={setSearchResults}
            setShowModal={setShowModal}
            handleReset={handleReset}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
            oriId={user.homeOriId?.toString()}
            officers={officers}
          />
          {loading && <div style={{ color: "black" }}>Loading...</div>}
          {error && (
            <div style={{ color: "black" }}>Error: {error?.message}</div>
          )}

          {!!searchResults && (
            <PaginatedTableWrapper
              id="paginated-table-wrapper"
              dashSize={dashSize}
              tableHeight={windowSize?.current?.innerHeight * 0.95 || 300}
              tableName={"Size and Weight Search Results"}
              headers={sizeAndWeightSearchHeaders}
              userId={user?.id}
              paginatedQueryHandler={handlePagePerResultAndPageChange}
              rows={
                searchResults
                  ? parseSizeAndWeightSearch(
                      searchResults,
                      handleIncidentOpening,
                      handleTicketOpening,
                      setShowModal,
                      user?.roleNames
                    )
                  : []
              }
              loading={loading}
              error={error}
              paginated={true}
              rowsPerPage={resultsPerPage || 10}
            />
          )}
        </form>
      </SearchModal>
    )
  );
};

export default SizeAndWeightSearchContainer;
